/**
 * Document display helpers — shared by the Decision Room's document
 * cards (BuyerSpaceProspectView) and the standalone room-document page
 * (RoomDocViewer).
 *
 * Lives here rather than in either component because the standalone
 * page is React.lazy: a helper imported FROM it would drag the whole
 * lazy chunk into the main bundle the moment the room view referenced
 * it, which is exactly the mistake the /d/ route was built to avoid.
 *
 * Buyer-facing literals on purpose. Every value below is a light-theme
 * hex, never a --room-* variable: these strings are consumed only by
 * prospect surfaces, which are pinned light so a seller's dark-mode
 * preference can't leak onto a customer's screen.
 */

// Ordered — first match wins, so the specific Office mimes beat the
// generic "application/*" tail. `accent` drives the branded
// placeholder that stands in for a missing thumbnail, so a grid of
// un-rendered documents still reads as a grid of documents rather
// than a wall of identical grey boxes.
const KINDS = [
  { label: 'PDF', accent: '#dc2626', test: (m, f) => m === 'application/pdf' || /\.pdf$/i.test(f) },
  {
    label: 'Word',
    accent: '#2563eb',
    test: (m, f) => /wordprocessingml|msword/i.test(m) || /\.docx?$/i.test(f),
  },
  {
    label: 'PowerPoint',
    accent: '#ea580c',
    test: (m, f) => /presentationml|ms-powerpoint/i.test(m) || /\.pptx?$/i.test(f),
  },
  {
    label: 'Excel',
    accent: '#059669',
    test: (m, f) => /spreadsheetml|ms-excel/i.test(m) || /\.xlsx?$/i.test(f),
  },
  { label: 'CSV', accent: '#0d9488', test: (m, f) => /csv/i.test(m) || /\.csv$/i.test(f) },
  { label: 'Image', accent: '#7c3aed', test: (m, f) => /^image\//i.test(m) || /\.(png|jpe?g|gif|webp|svg)$/i.test(f) },
  { label: 'Archive', accent: '#64748b', test: (m, f) => /zip|compressed/i.test(m) || /\.(zip|rar|7z)$/i.test(f) },
  { label: 'Text', accent: '#475569', test: (m, f) => /^text\//i.test(m) || /\.(txt|md|rtf)$/i.test(f) },
];

const FALLBACK_KIND = { label: 'File', accent: '#94a3b8' };

/**
 * Friendly file-type label + a stable accent for one document.
 * Mime first (the server knows it), filename extension as the
 * tiebreaker for the handful of rows whose mime landed as
 * application/octet-stream.
 */
export function fileKind(mimeType, filename) {
  const m = (mimeType || '').toString();
  const f = (filename || '').toString();
  const hit = KINDS.find((k) => k.test(m, f));
  return hit ? { label: hit.label, accent: hit.accent } : FALLBACK_KIND;
}

/** Human byte size. Returns '' for null/0 so callers can filter it out. */
export function formatBytes(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(0)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

/** "4 pages" / "1 page" / '' when the server doesn't know yet. */
export function formatPageCount(pageCount) {
  const n = Number(pageCount);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${n} page${n === 1 ? '' : 's'}`;
}

/** Base URL for every per-document endpoint on a room doc. */
export function roomDocBase(apiBase, slug, docId) {
  return `${apiBase}/api/buyer-spaces/public/${encodeURIComponent(slug)}/docs/${encodeURIComponent(docId)}`;
}

/** The standalone reading page for one room document. */
export function roomDocPageUrl(slug, docId, token) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/space/${encodeURIComponent(slug)}/doc/${encodeURIComponent(docId)}`
    + `?t=${encodeURIComponent(token)}`;
}
